import React, { useRef, useEffect } from "react";
import { getSlotControllers, hasSlotController, PluginRuntime, pluginLog } from "@markedesk/plugin-sdk/ui";
import { useChannelPlugins } from "../ChannelIcon";
import { mountPluginJsx } from "./pluginJsx";
import api from "../../services/api";

// Carregamento sob demanda do @babel/standalone — só baixa quando um plugin usa
// JSX (slot.mountJsx). Não incha o bundle do app. Servido LOCALMENTE pelo app
// (public/vendor/babel.min.js); o browser cacheia o arquivo. Fallback ao CDN se
// o arquivo local não existir. Cacheia a Promise (carrega 1x por sessão).
const BABEL_LOCAL = "/vendor/babel.min.js";
const BABEL_CDN = "https://unpkg.com/@babel/standalone@7/babel.min.js";
let babelPromise = null;
function injectScript(src) {
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = src;
    s.onload = () => resolve(window.Babel);
    s.onerror = () => reject(new Error("Falha ao carregar " + src));
    document.head.appendChild(s);
  });
}
function loadBabel() {
  if (window.Babel) return Promise.resolve(window.Babel);
  if (babelPromise) return babelPromise;
  // Tenta o local (cacheado pelo browser); se falhar, cai pro CDN.
  babelPromise = injectScript(BABEL_LOCAL)
    .catch(() => injectScript(BABEL_CDN))
    .catch((e) => { babelPromise = null; throw e; });
  return babelPromise;
}

/**
 * Abre um modal via DOM direto (sem depender do PluginHtmlModalHost nem de
 * instância de módulo). Cria um overlay fixo + caixa branca, injeta o html e
 * chama onMount(content, resolve, close). Retorna Promise que resolve com o
 * valor passado a resolve() (ou undefined ao fechar).
 */
function openDomModal(opts) {
  return new Promise((resolve) => {
    let done = false;
    const finish = (v) => { if (done) return; done = true; overlay.remove(); resolve(v); };

    const overlay = document.createElement("div");
    overlay.style.cssText =
      "position:fixed;inset:0;background:rgba(0,0,0,.4);z-index:13000;" +
      "display:flex;align-items:center;justify-content:center;padding:16px";
    overlay.addEventListener("click", (e) => { if (e.target === overlay) finish(undefined); });

    // opts.width segue o mesmo contrato tipado do SDK (OpenHtmlModalOptions,
    // ver packages/plugin-sdk/src/ui/hooks.ts) que openHtmlModal/
    // PluginHtmlModalHost já respeitam via MUI Dialog maxWidth — replica os
    // mesmos breakpoints aqui pro slot.openModal (PluginWrap) NÃO ficar
    // desalinhado da outra implementação. Sem width → default "sm" (520px,
    // idêntico ao comportamento anterior, retrocompat total).
    const WIDTH_PX = { xs: 444, sm: 600, md: 900, lg: 1200, xl: 1536 };
    const maxWidth = (WIDTH_PX[opts.width] || 520) + "px";
    const box = document.createElement("div");
    box.style.cssText =
      "background:#fff;border-radius:12px;max-width:" + maxWidth + ";width:100%;" +
      "max-height:85vh;overflow:auto;box-shadow:0 12px 40px rgba(0,0,0,.25)";

    if (opts.title) {
      const h = document.createElement("div");
      h.style.cssText = "padding:14px 18px;border-bottom:1px solid #eee;font-weight:600;font-size:16px";
      h.textContent = opts.title;
      box.appendChild(h);
    }
    const content = document.createElement("div");
    content.style.cssText = "padding:18px";
    box.appendChild(content);
    overlay.appendChild(box);
    document.body.appendChild(overlay);

    // Modo REACT/JSX: transpila (Babel) e monta o React do app (hooks funcionam).
    // O componente recebe props + { resolve, close } para fechar o modal.
    if (opts.jsx) {
      const cache = {};
      mountPluginJsx(
        content,
        opts.jsx,
        { ...(opts.props || {}), resolve: (v) => finish(v), close: () => finish(undefined) },
        cache
      ).catch((e) => { /* eslint-disable-line no-console */ console.error("[PluginWrap modal] jsx erro:", e); });
      return;
    }

    // Modo HTML (legado): innerHTML + onMount.
    content.innerHTML = opts.html || "";
    if (typeof opts.onMount === "function") {
      try { opts.onMount(content, (v) => finish(v), () => finish(undefined)); }
      catch (e) { /* eslint-disable-line no-console */ console.error("[PluginWrap modal] onMount erro:", e); }
    }
  });
}

/**
 * PluginWrap — envolve um elemento do app e entrega a um plugin a API imperativa
 * para gerenciá-lo (hide/show/remove/add/clear) + eventos de ciclo de vida
 * (onMount/onContextChange/onUnmount). Mecanismo genérico de "wrap".
 *
 * Os controladores são registrados pelos plugins via markedesk.registerSlot()
 * (frontendScript serializado). Tudo opera sobre DOM real → funciona buildado.
 *
 * Sem controlador para o slot → renderiza children direto (custo ~zero).
 *
 * Props:
 *   slot     — nome do slot (ex: "message-input")
 *   context  — dados entregues ao controlador (ticketId, channel, ...)
 *   children — o elemento original do app
 */
export default function PluginWrap({ slot, context = {}, children }) {
  const plugins = useChannelPlugins();
  const originalRef = useRef(null);   // wrapper do elemento original
  const layerRef = useRef(null);      // camada onde o plugin injeta elementos
  const slotApisRef = useRef([]);     // SlotApi por controlador (para lifecycle)
  const hasControllers = hasSlotController(slot);
  pluginLog("PluginWrap render", { slot, hasControllers, plugins: plugins.length, ticketId: context.ticketId, channel: context.channel });

  // Constrói a SlotApi para um controlador de um plugin específico.
  const makeSlotApi = (pluginId) => {
    const plugin = plugins.find((p) => p.id === pluginId || p.channel === pluginId);
    const added = [];
    const apiObj = {
      context,
      routePath: plugin?.routePath || null,
      api,
      hide() { if (originalRef.current) originalRef.current.style.display = "none"; },
      show() { if (originalRef.current) originalRef.current.style.display = ""; },
      remove() { if (originalRef.current) originalRef.current.innerHTML = ""; },
      add(el) {
        const node = document.createElement("div");
        if (el.html != null) node.innerHTML = el.html;
        if (typeof el.onClick === "function") {
          node.style.cursor = "pointer";
          node.addEventListener("click", () => el.onClick(apiObj));
        }
        layerRef.current && layerRef.current.appendChild(node);
        if (typeof el.onMount === "function") {
          try { el.onMount(node, apiObj); } catch (e) { /* eslint-disable-line */ }
        }
        const handle = { remove() { node.remove(); } };
        added.push(handle);
        return handle;
      },
      clear() {
        added.splice(0).forEach((h) => h.remove());
        // Desmonta qualquer árvore React montada pelo plugin antes de limpar.
        if (apiObj.__reactNode && PluginRuntime.reactDOM) {
          try { PluginRuntime.reactDOM.unmountComponentAtNode(apiObj.__reactNode); } catch (e) { /* noop */ }
          try { apiObj.__reactNode.remove(); } catch (e) { /* noop */ }
          apiObj.__reactNode = null;
        }
        if (layerRef.current) layerRef.current.innerHTML = "";
        apiObj.show();
      },
      /**
       * Monta um componente REACT de verdade no slot, usando o React DO APP
       * (PluginRuntime.react) — mesma versão, sem bundle do plugin. `factory`
       * é `(React, ctx) => ReactElement`. Esconde o elemento original e renderiza
       * a árvore React num container próprio. Reidempotente (re-render no mesmo nó).
       */
      mount(factory) {
        const React = PluginRuntime.react;
        const ReactDOM = PluginRuntime.reactDOM;
        if (!React || !ReactDOM || typeof factory !== "function") return;
        // Remove HTML solto (slot.add) antes de montar React no mesmo slot.
        added.splice(0).forEach((h) => h.remove());
        if (!apiObj.__reactNode) {
          apiObj.__reactNode = document.createElement("div");
          apiObj.__reactNode.style.cssText = "display:flex;flex:1;width:100%";
          layerRef.current && layerRef.current.appendChild(apiObj.__reactNode);
        }
        apiObj.hide(); // esconde o indicador nativo enquanto o plugin renderiza
        try {
          ReactDOM.render(factory(React, apiObj.context), apiObj.__reactNode);
        } catch (e) { /* eslint-disable-line no-console */ console.error("[PluginWrap] mount erro", e); }
      },
      /**
       * Monta um componente escrito em JSX (string). O Babel (CDN, sob demanda)
       * transpila o JSX → createElement e renderiza com o React DO APP. Assim o
       * plugin escreve React "igual TS" (JSX), sem build próprio.
       *
       * `jsx` é o corpo de uma função `(React, props) => <JSX/>`. Ex.:
       *   slot.mountJsx("return <div>{props.message}</div>", props)
       */
      async mountJsx(jsx, props) {
        try {
          const Babel = await loadBabel();
          const React = PluginRuntime.react;
          const ReactDOM = PluginRuntime.reactDOM;
          if (!Babel || !React || !ReactDOM) return;

          // Compila a função UMA VEZ por slot (cacheia por string JSX). Re-eval a
          // cada update recriaria o tipo do componente → o React remontaria e o
          // useState/useEffect RESETARIAM. Compilando uma vez e re-renderizando
          // com props novas, o React preserva estado/effects do dev. "Coisas do
          // React" (useState, useEffect, useRef, context) funcionam — é o React
          // do app (mesma instância).
          if (!apiObj.__jsxFns) apiObj.__jsxFns = {};
          let fn = apiObj.__jsxFns[jsx];
          if (!fn) {
            const { code } = Babel.transform(
              "(function(React, props){ " + jsx + " })",
              { presets: ["react"] }
            );
            // eslint-disable-next-line no-eval
            fn = (0, eval)(code);
            apiObj.__jsxFns[jsx] = fn;
          }

          // Remove HTML solto (slot.add) antes de montar React no mesmo slot.
          added.splice(0).forEach((h) => h.remove());
          if (!apiObj.__reactNode) {
            apiObj.__reactNode = document.createElement("div");
            apiObj.__reactNode.style.cssText = "display:flex;flex:1;width:100%";
            layerRef.current && layerRef.current.appendChild(apiObj.__reactNode);
          }
          apiObj.hide();
          // Componente estável (criado UMA vez) → o React reconcilia e preserva
          // estado/effects entre renders. Ele delega ao fn compilado (que contém
          // o corpo do dev, com hooks no topo). Como é o mesmo tipo de componente
          // a cada render, useState/useEffect persistem.
          if (!apiObj.__jsxComp) {
            apiObj.__jsxComp = (p) => apiObj.__jsxFns[apiObj.__jsxKey](React, p);
          }
          apiObj.__jsxKey = jsx;
          ReactDOM.render(
            React.createElement(apiObj.__jsxComp, props || apiObj.context),
            apiObj.__reactNode
          );
        } catch (e) { /* eslint-disable-line no-console */ console.error("[PluginWrap] mountJsx erro", e); }
      },
      openModal(opts) { return openDomModal(opts); },
      onTicketMessage(ticketId, cb) { return PluginRuntime.onTicketMessage(ticketId, cb); },
    };
    return apiObj;
  };

  // Mount / unmount dos controladores.
  useEffect(() => {
    pluginLog("PluginWrap mount effect", { slot, hasControllers, ctx: context });
    if (!hasControllers) return undefined;
    let controllers = getSlotControllers(slot);
    // GUARD genérico: no slot de status de conexão, só monta o controller do
    // plugin DAQUELE canal — senão o slot de um plugin (ex: botão "Conectar" do
    // wwebjs) vaza para conexões de OUTROS canais (whatsapp nativo, baileys). O
    // pluginId do controller deve casar com o channel/id da conexão. #4137
    if ((slot === "connection-status" || slot === "connection-actions") && context.channel) {
      // O pluginId do controller de um canal é o próprio channel (plugin.id ===
      // channel, ex: "whatsapp_wwebjs"). Só monta quem provê o canal da linha.
      controllers = controllers.filter(({ pluginId }) => pluginId === context.channel);
    }
    pluginLog("PluginWrap onMount controllers", { slot, count: controllers.length });
    const apis = controllers.map(({ pluginId, controller }) => {
      const slotApi = makeSlotApi(pluginId);
      pluginLog("PluginWrap chamando onMount", { pluginId, routePath: slotApi.routePath });
      try { controller.onMount && controller.onMount(slotApi); } catch (e) { /* eslint-disable-line no-console */ console.error("[PluginWrap] erro onMount", e); }
      return { slotApi, controller };
    });
    slotApisRef.current = apis;
    return () => {
      apis.forEach(({ slotApi, controller }) => {
        try { controller.onUnmount && controller.onUnmount(slotApi); } catch (e) { /* eslint-disable-line */ }
      });
      slotApisRef.current = [];
    };
    // plugins.length: quando o /plugins resolve DEPOIS do mount (1ª carga),
    // os controladores são registrados tardiamente; reagir à lista garante que
    // o efeito re-rode e o controlador seja montado (senão o wrap só apareceria
    // após reload).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slot, hasControllers, plugins.length]);

  // Mudança de contexto → re-decide (atualiza a referência e notifica).
  useEffect(() => {
    pluginLog("PluginWrap contextChange effect", { slot, apis: slotApisRef.current.length, ticketId: context.ticketId });
    slotApisRef.current.forEach(({ slotApi, controller }) => {
      slotApi.context = context;
      try { controller.onContextChange && controller.onContextChange(slotApi); } catch (e) { /* eslint-disable-line no-console */ console.error("[PluginWrap] erro onContextChange", e); }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(context)]);

  // Sem controladores → passa direto (sem overhead nem containers extras).
  if (!hasControllers) return children;

  return (
    <div style={{ display: "flex", flex: 1, alignItems: "center", width: "100%" }}>
      <div ref={originalRef} style={{ display: "flex", flex: 1, width: "100%" }}>
        {children}
      </div>
      <div ref={layerRef} style={{ display: "contents" }} />
    </div>
  );
}
